import React, { useState, useEffect, useCallback, useRef } from 'react';
import { publicListsAPI } from '../services/api';
import './PublicList.css';

const NAME_KEY = 'shared_list_my_name';
const POLL_MS = 8000;

function readMyName() {
  try {
    return localStorage.getItem(NAME_KEY) || '';
  } catch (e) {
    return '';
  }
}

// Public, no-login checklist page reached via /list/<token>
function PublicList({ token }) {
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [newItem, setNewItem] = useState('');
  const [myName, setMyName] = useState(readMyName);
  const [drafts, setDrafts] = useState({});
  const listRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const response = await publicListsAPI.get(token);
      setList(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.status === 404 ? 'This list does not exist or was deleted.' : 'Could not load the list.');
    }
  }, [token]);

  useEffect(() => {
    load();
    // Keep in sync with friends' edits, but don't refresh while someone is typing in the list
    const interval = setInterval(() => {
      if (listRef.current && listRef.current.contains(document.activeElement)) return;
      if (document.visibilityState === 'visible') load();
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    if (list?.title) document.title = list.title;
  }, [list?.title]);

  const saveMyName = (value) => {
    setMyName(value);
    try {
      localStorage.setItem(NAME_KEY, value);
    } catch (e) {}
  };

  const replaceItem = (item) => {
    setList((prev) => ({ ...prev, items: prev.items.map((i) => (i.id === item.id ? item : i)) }));
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const name = newItem.trim();
    if (!name) return;
    try {
      const response = await publicListsAPI.addItem(token, { name });
      setList((prev) => ({ ...prev, items: [...prev.items, response.data] }));
      setNewItem('');
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not add item');
    }
  };

  const updateItem = async (item, data) => {
    try {
      const response = await publicListsAPI.updateItem(token, item.id, data);
      replaceItem(response.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not update item');
      load();
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Remove "${item.name}"?`)) return;
    try {
      await publicListsAPI.deleteItem(token, item.id);
      setList((prev) => ({ ...prev, items: prev.items.filter((i) => i.id !== item.id) }));
    } catch (err) {
      alert('Could not remove item');
    }
  };

  const commitAssignee = (item) => {
    const draft = drafts[item.id];
    setDrafts(({ [item.id]: _, ...rest }) => rest);
    if (draft === undefined || draft.trim() === (item.assignee || '')) return;
    updateItem(item, { assignee: draft });
  };

  if (error) {
    return (
      <div className="public-list-page">
        <div className="public-list-card public-list-message">{error}</div>
      </div>
    );
  }

  if (!list) {
    return (
      <div className="public-list-page">
        <div className="public-list-card public-list-message">Loading...</div>
      </div>
    );
  }

  const items = [...list.items].sort((a, b) => Number(a.is_done) - Number(b.is_done));
  const doneCount = list.items.filter((i) => i.is_done).length;
  const unassignedCount = list.items.filter((i) => !i.is_done && !i.assignee).length;

  return (
    <div className="public-list-page">
      <div className="public-list-card">
        <header className="public-list-header">
          <h1>🛒 {list.title}</h1>
          <p className="public-list-stats">
            {doneCount}/{list.items.length} bought
            {unassignedCount > 0 && <> · {unassignedCount} without anyone in charge</>}
          </p>
        </header>

        <label className="public-list-myname">
          <span>Your name</span>
          <input
            type="text"
            value={myName}
            maxLength={100}
            placeholder="So you can grab items in one tap"
            onChange={(e) => saveMyName(e.target.value)}
          />
        </label>

        <form className="public-list-add" onSubmit={handleAdd}>
          <input
            type="text"
            value={newItem}
            maxLength={200}
            placeholder="Add a grocery (e.g. Eggs x12)"
            onChange={(e) => setNewItem(e.target.value)}
          />
          <button type="submit" disabled={!newItem.trim()}>Add</button>
        </form>

        {items.length === 0 ? (
          <p className="public-list-empty">Nothing yet. Add the first item above.</p>
        ) : (
          <ul className="public-list-items" ref={listRef}>
            {items.map((item) => {
              const assigneeValue = drafts[item.id] ?? item.assignee ?? '';
              return (
                <li key={item.id} className={item.is_done ? 'done' : ''}>
                  <input
                    type="checkbox"
                    className="public-list-check"
                    checked={item.is_done}
                    onChange={() => updateItem(item, { is_done: !item.is_done })}
                    aria-label={`Mark ${item.name} as bought`}
                  />
                  <span className="public-list-name">{item.name}</span>
                  <div className="public-list-assignee">
                    <input
                      type="text"
                      value={assigneeValue}
                      maxLength={100}
                      placeholder="Who's in charge?"
                      onChange={(e) => setDrafts((d) => ({ ...d, [item.id]: e.target.value }))}
                      onBlur={() => commitAssignee(item)}
                      onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                    />
                    {!item.is_done && !item.assignee && myName.trim() && drafts[item.id] === undefined && (
                      <button
                        type="button"
                        className="public-list-take"
                        onClick={() => updateItem(item, { assignee: myName })}
                      >
                        Me
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    className="public-list-delete"
                    onClick={() => handleDelete(item)}
                    aria-label={`Remove ${item.name}`}
                  >
                    ✕
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <p className="public-list-footer">Anyone with this link can edit this list.</p>
      </div>
    </div>
  );
}

export default PublicList;
